import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { AddTicketsData } from "../../../common";
import { USE_LIFESTYLE_MOCKS } from "./lifestyle.mocks";
import type {
  AddTicketsForAllCompetitionsParams,
  AddTicketsParams,
  GetDailyPrizesParams,
  GetPrizeCollectionsParams,
  LifestyleCollectionsResponse,
  PrizeSearchResponse,
} from "./lifestyle.types";

export type {
  AddTicketsForAllCompetitionsParams,
  AddTicketsParams,
  GetDailyPrizesParams,
  GetPrizeCollectionsParams,
  LifestyleCollectionDto,
  LifestyleCollectionsResponse,
  LifestylePrize,
  PrizeSearchResponse,
  TicketProduct
} from "./lifestyle.types";

/** Matches PrizeSearchSortOrder in BotB.Core.Models. */
export const PRIZE_SEARCH_SORT_ORDER = {
  Recommended: 0,
  TicketPriceDescending: 1,
  TicketPriceAscending: 2,
  ManualSortOrderingDescending: 4,
  AlphabeticalAscending: 5,
  AlphabeticalDescending: 6,
} as const;

export const lifestyleApi = createApi({
  reducerPath: "lifestyleApi",
  // credentials are required: prize pricing, multibuy eligibility and
  // UserCanPurchase are all resolved from the session on the server.
  baseQuery: fetchBaseQuery({ baseUrl: process.env.NEXT_PUBLIC_HOST, credentials: "include" }),
  tagTypes: ["LifestylePrizes", "LifestyleCollections", "Cart"],
  endpoints: build => ({
    getDailyPrizes: build.query<PrizeSearchResponse, GetDailyPrizesParams>({
      query: ({ prizeCategory, competitionId, activeCompetitions }) => ({
        url: "/umbraco/botb/lifestylecomp/prizesearchdaily",
        params: {
          prizeCategory,
          competitionId,
          activeCompetitions,
          sortOrder: PRIZE_SEARCH_SORT_ORDER.Recommended,
          // 0 means "return everything"; the page filters and pages client-side.
          quantityOfResults: 0,
          isCompetitionPage: true,
        },
      }),
      providesTags: ["LifestylePrizes"],
    }),

    getPrizeCollections: build.query<LifestyleCollectionsResponse, GetPrizeCollectionsParams>({
      queryFn: async ({ competitionId }, _api, _extraOptions, baseQuery) => {
        if (USE_LIFESTYLE_MOCKS) {
          return { data: { Collections: [], Banners: [] } };
        }

        const result = await baseQuery({
          url: "/umbraco/00000001/app/LifestyleCollections",
          params: { competitionId },
        });

        if (result.error) return { error: result.error };
        return { data: result.data as LifestyleCollectionsResponse };
      },
      providesTags: ["LifestyleCollections"],
    }),

    addTickets: build.mutation<AddTicketsData, AddTicketsParams>({
      query: ({ competitionId, prizeContentNodeId, quantityToAdd, addToCartMethodId, answerId }) => ({
        url: "/umbraco/botb/cart/addtickets",
        method: "POST",
        params: { competitionId, prizeContentNodeId, quantityToAdd, addToCartMethodId, answerId },
      }),
      invalidatesTags: ["Cart"],
    }),

    /** Daily prizes can span several competitions; this enters them all at once. */
    addTicketsForAllCompetitions: build.mutation<AddTicketsData, AddTicketsForAllCompetitionsParams>({
      query: ({ competitionIdListString, prizeContentNodeId, quantityToAdd, addToCartMethodId, answerId }) => ({
        url: "/umbraco/botb/cart/addTicketsForAllCompetitions",
        method: "POST",
        params: { competitionIdListString, prizeContentNodeId, quantityToAdd, addToCartMethodId, answerId },
      }),
      invalidatesTags: ["Cart"],
    }),
  }),
});

export const {
  useGetDailyPrizesQuery,
  useGetPrizeCollectionsQuery,
  useAddTicketsMutation,
  useAddTicketsForAllCompetitionsMutation,
} = lifestyleApi;
